/**
 * Set HTTP `Cache-Control` on the current SSR response so Cloudflare's edge
 * can serve a cached HTML copy on subsequent visits. This is what fixes the
 * field Core Web Vitals (TTFB cascading into FCP/LCP) without touching the
 * Lighthouse lab score.
 *
 * Safe to call from any TanStack `loader`:
 *   - On the server (SSR): sets the response header.
 *   - On the client (navigation, prefetch): no-op.
 *   - If the underlying server module is unavailable for any reason, swallows
 *     the error so a header miss never breaks a page render.
 *
 * Defaults: cache for 5 minutes at the edge, serve stale for 24 hours while
 * revalidating. Override per-route when content changes more / less often.
 */
import { createIsomorphicFn } from "@tanstack/react-start";
import {
  setResponseHeader as ssrSetResponseHeader,
  setResponseStatus as ssrSetResponseStatus,
} from "@tanstack/react-start/server";

const setCacheHeaderServer = createIsomorphicFn()
  .client((_value: string) => {})
  .server((value: string) => {
    try {
      ssrSetResponseHeader("Cache-Control", value);
    } catch {
      // Outside a server request context (rare). Silently skip, page still renders.
    }
  });

export async function setEdgeHtmlCache(
  opts: { sMaxAge?: number; staleWhileRevalidate?: number } = {},
): Promise<void> {
  if (typeof window !== "undefined") return;
  const sMaxAge = opts.sMaxAge ?? 300;
  const swr = opts.staleWhileRevalidate ?? 86_400;
  await setCacheHeaderServer(
    `public, max-age=0, s-maxage=${sMaxAge}, stale-while-revalidate=${swr}`,
  );
}

/**
 * Send an HTTP 404 status + `X-Robots-Tag: noindex, nofollow` on the current
 * SSR response. Safe to call from a component render body: on the client it is
 * a no-op; on the server it sets response headers before the shell flushes.
 * The matching `<meta name="robots" content="noindex, nofollow">` is emitted
 * once by the root `head()` from the not-found match status.
 */
const setNotFoundServer = createIsomorphicFn()
  .client(() => {})
  .server(() => {
    try {
      ssrSetResponseStatus(404);
      ssrSetResponseHeader("X-Robots-Tag", "noindex, nofollow");
      ssrSetResponseHeader("Cache-Control", "no-store");
    } catch {
      // Outside SSR request context, page still renders.
    }
  });

export function markNotFoundResponse(): void {
  if (typeof window !== "undefined") return;
  setNotFoundServer();
}
